import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const serviceLinks = [
    { name: 'Application Development Services', path: '/application-development' },
    { name: 'Web Development Services', path: '/web-development' },
    { name: 'Mobile App Development', path: '/mobile-app-development' },
    { name: 'AI & Machine Learning Development', path: '/ai-ml-development' },
    { name: 'Dedicated Developers / On-Demand Teams', path: '/hire-developers' },
  ];

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services', dropdown: true },
    { name: 'Case Studies', path: '/case-studies' },
    { name: 'Blog', path: 'https://www.nvseeds.com/blog', external: true },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-glass border-b border-[#E2E8F0]/90">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="/nvseeds-logo.png" 
              alt="Novaseeds Technologies" 
              className="h-32 md:h-40 w-auto"
            />
          </Link>

         <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => {
              if (link.external) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-white/60 transition-colors"
                  >
                    {link.name}
                  </a>
                );
              }

              if (link.dropdown) {
                return (
                  <div key={link.path} className="relative group">
                    <Link
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="desktop-nav-services"
                      className={`flex items-center gap-1.5 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        location.pathname === link.path || serviceLinks.some((item) => item.path === location.pathname)
                          ? 'text-blue-600 bg-blue-50/90'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                      }`}
                    >
                      Services
                      <ChevronDown size={14} className="transition-transform duration-150 group-hover:rotate-180" />
                    </Link>
                    <div className="absolute top-full left-0 mt-2 w-80 rounded-2xl border border-[#E2E8F0] bg-white shadow-lg opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150 z-50">
                      <div className="p-2">
                        {serviceLinks.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`block rounded-xl px-3 py-2 text-sm transition-colors ${
                              location.pathname === item.path
                                ? 'bg-blue-50 text-blue-600'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                            }`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-blue-600 bg-blue-50/90'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2.5 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)] transition-all hover:scale-105 active:scale-95"
            >
              Get Started
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-slate-800 hover:bg-white/60 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-[#E2E8F0]/90 bg-white/75 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => {
                if (link.external) {
                  return (
                    <a
                      key={link.path}
                      href={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 transition-colors"
                    >
                      {link.name}
                    </a>
                  );
                }

                if (link.dropdown) {
                  return (
                    <div key={link.path}>
                      <Link
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                          location.pathname === link.path
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                        }`}
                      >
                        {link.name}
                      </Link>
                      <div className="ml-4 mt-1 space-y-1">
                        {serviceLinks.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block px-4 py-2 rounded-lg text-sm transition-colors ${
                              location.pathname === item.path
                                ? 'text-blue-600 bg-blue-50'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                            }`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.path
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-cta-button"
                className="block text-center bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-3 font-semibold mt-4"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
